import { getApiBaseUrl } from '../config/env';
import { mapPublicTermsPayload, hasTermsContent } from './mapPublicTerms';

const CACHE_KEY = 'jobscribe_public_terms_v1';
const CACHE_TTL_MS = 10 * 60 * 1000;

/** @type {Promise<ReturnType<typeof mapPublicTermsPayload>> | null} */
let inflightRequest = null;

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.fetchedAt) return null;
    if (Date.now() - parsed.fetchedAt > CACHE_TTL_MS) return null;
    return mapPublicTermsPayload(parsed);
  } catch {
    return null;
  }
}

function writeCache(payload) {
  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        fetchedAt: Date.now(),
        hero: payload.hero,
        sections: payload.sections,
        cta: payload.cta,
      })
    );
  } catch {
    // ignore
  }
}

export function getCachedPublicTerms() {
  return readCache();
}

/**
 * @param {{ forceRefresh?: boolean }} [options]
 */
export async function fetchPublicTerms(options = {}) {
  const { forceRefresh = false } = options;

  if (!forceRefresh) {
    const cached = readCache();
    if (cached && hasTermsContent(cached)) return cached;
  }

  if (inflightRequest) return inflightRequest;

  inflightRequest = (async () => {
    const res = await fetch(`${getApiBaseUrl()}/public/terms`, {
      credentials: 'omit',
    });
    if (!res.ok) {
      throw new Error('Failed to fetch terms');
    }
    const json = await res.json();
    const data = json.data ?? json;
    const payload = mapPublicTermsPayload(data);
    if (hasTermsContent(payload)) {
      writeCache(payload);
    }
    return payload;
  })();

  try {
    return await inflightRequest;
  } finally {
    inflightRequest = null;
  }
}

export function prefetchPublicTerms() {
  const cached = readCache();
  if (cached && hasTermsContent(cached)) return;
  void fetchPublicTerms().catch(() => {});
}
